var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useMemo } from 'react';
import { sprintf } from 'sprintf-js';
import { makeStyles, Checkbox, Dialog, DialogTitle, DialogActions, Container, DialogButton, Typography, useAlert, } from "../../../index";
import { useUseCase } from "../../../hooks/useUseCase";
import { useTranslation } from "../../../hooks/useTranslation";
import { useAuth } from "../../../hooks/useAuth";
import { useDeclarativeLoading } from "../../../hooks/useLoading";
import { initialNoteSelectDialogState } from "./SnapshotNoteSelectDialogState";
import { OpenNoteSelectDialogUseCase } from "./usecases/OpenNoteSelectDialogUseCase";
import { NoteSelectDialogToolbar } from "./components/NoteSelectDialogToolbar";
import { UnselectNoteUseCase } from "./usecases/UnselectNoteUseCase";
import { SelectNoteUseCase } from "./usecases/SelectNoteUseCase";
import { ChangePaginationUseCase } from "./usecases/ChangePaginationUseCase";
import { CloseNoteSelectDialogUseCase } from "./usecases/CloseNoteSelectDialogUseCase";
import { NoteSelectDialogContent } from "./components/NoteSelectDialogContent";
import { ChangeOwnerNameVisibilityUseCase } from "./usecases/ChangeOwnerNameVisibilityUseCase";
import { ChangeFlagPageVisibilityUseCase } from "./usecases/ChangeFlagPageVisibilityUseCase";
import { CloseAlertUseCase } from "./usecases/CloseAlertUseCase";
var useStyles = makeStyles(function (theme) { return ({
    root: {},
    container: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        overflow: 'hidden',
        maxWidth: 'none',
        padding: 0,
    },
    actions: {
        position: 'relative',
        height: 100,
    },
    count: {
        position: 'absolute',
        left: theme.spacing(3),
        top: '50%',
        transform: 'translate(0, -50%)',
    },
    checkboxArea: {
        position: 'absolute',
        right: theme.spacing(3),
        top: '50%',
        transform: 'translate(0, -50%)',
        display: 'flex',
        flexDirection: 'column',
    },
    checkbox: {
        margin: theme.spacing(1, 0),
    },
}); });
export var SnapshotNoteSelectDialog = function (props) {
    var open = props.open, snapshotNoteId = props.snapshotNoteId, maxSelection = props.maxSelection, maxSelectionMessage = props.maxSelectionMessage, onSelect = props.onSelect, onCancel = props.onCancel, others = __rest(props, ["open", "snapshotNoteId", "maxSelection", "maxSelectionMessage", "onSelect", "onCancel"]);
    var _a = useUseCase(initialNoteSelectDialogState), state = _a.state, usecase = _a.usecase;
    var t = useTranslation().t;
    var classes = useStyles();
    // ノートの読み込み中にローディングを表示する
    useDeclarativeLoading('loading', state.isLoading);
    var user = useAuth().user;
    var userId = useMemo(function () { return "" + (user === null || user === void 0 ? void 0 : user.userId); }, [user === null || user === void 0 ? void 0 : user.userId]);
    var isTeacher = useMemo(function () { return (user === null || user === void 0 ? void 0 : user.userRole) === 'ADMINISTRATOR_TEACHER' || (user === null || user === void 0 ? void 0 : user.userRole) === 'TEACHER'; }, [
        user === null || user === void 0 ? void 0 : user.userRole,
    ]);
    useEffect(function () {
        open
            ? usecase(new OpenNoteSelectDialogUseCase()).execute({
                snapshotNoteId: snapshotNoteId,
                userId: userId,
                isTeacher: isTeacher,
            })
            : usecase(new CloseNoteSelectDialogUseCase()).execute();
    }, [open, usecase, snapshotNoteId, userId, isTeacher]);
    var handleCancel = useCallback(function () {
        onCancel === null || onCancel === void 0 ? void 0 : onCancel();
    }, [onCancel]);
    var alert = useAlert().alert;
    var handleSelect = useCallback(function () {
        var count = state.selectedNotes.notes.length;
        if (count === 0)
            return;
        // 選択件数の上限チェック
        if (count > maxSelection) {
            alert(maxSelectionMessage);
            handleCancel();
            return;
        }
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(state.selectedNotes.notes, state.showOwnerName, state.showFlagPage);
    }, [
        alert,
        handleCancel,
        maxSelection,
        maxSelectionMessage,
        onSelect,
        state.selectedNotes.notes,
        state.showFlagPage,
        state.showOwnerName,
    ]);
    var handleSelectNote = useCallback(function (note, selected) {
        selected
            ? usecase(new UnselectNoteUseCase()).execute(note)
            : usecase(new SelectNoteUseCase()).execute(note);
    }, [usecase]);
    var handleChangePagination = useCallback(function (nextPage) {
        usecase(new ChangePaginationUseCase()).execute(nextPage);
    }, [usecase]);
    var handleChangeOwnerNameVisibility = useCallback(function (showOwnerName) {
        usecase(new ChangeOwnerNameVisibilityUseCase()).execute(showOwnerName);
    }, [usecase]);
    var handleChangeFlagPageVisibility = useCallback(function (showFlagPage) {
        usecase(new ChangeFlagPageVisibilityUseCase()).execute(showFlagPage);
    }, [usecase]);
    useEffect(function () {
        if (state.requiredAlert) {
            alert(t(state.requiredAlert.errorMessage)).then(function () {
                usecase(new CloseAlertUseCase()).execute();
            });
        }
    }, [alert, state.requiredAlert, t, usecase]);
    return (React.createElement(Dialog, __assign({ className: classes.root, open: open, fullScreen: true }, others),
        React.createElement(DialogTitle, null, t('ノートを選ぶ')),
        React.createElement(Container, { className: classes.container },
            React.createElement(NoteSelectDialogToolbar, { pagination: state.pagination, onChangePagination: handleChangePagination }),
            React.createElement(NoteSelectDialogContent, { notes: state.currentNotes, onSelectNote: handleSelectNote, selectedNoteId: state.selectedNoteId, showOwnerName: state.showOwnerName, showFlagPage: state.showFlagPage, isLoading: state.isLoading })),
        React.createElement(DialogActions, { className: classes.actions },
            React.createElement(Typography, { className: classes.count }, sprintf(t('選んだノート %s件'), state.selectedNoteId.length)),
            React.createElement(DialogButton, { onClick: handleCancel }, t('キャンセル')),
            React.createElement(DialogButton, { fill: true, onClick: handleSelect, disabled: state.selectedNoteId.length === 0 }, t('選ぶ')),
            React.createElement("div", { className: classes.checkboxArea },
                React.createElement("div", { className: classes.checkbox },
                    React.createElement(Checkbox, { label: t('所有者名を表示する'), value: state.showOwnerName, onChange: handleChangeOwnerNameVisibility, disabled: !state.isAssignmentOwner })),
                React.createElement("div", { className: classes.checkbox },
                    React.createElement(Checkbox, { label: t('旗のついたページを表示する'), value: state.showFlagPage, onChange: handleChangeFlagPageVisibility }))))));
};
